export class JsonRequestError extends Error {
  constructor(
    message: string,
    readonly status?: number
  ) {
    super(message)
    this.name = 'JsonRequestError'
  }
}

type JsonRequestOptions = Omit<RequestInit, 'body' | 'credentials' | 'mode'> & {
  body?: unknown
}

const unexpectedResponse =
  'The server returned an unexpected response. Refresh the page and try again.'

export function csrfToken(): string | undefined {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    ?.content
}

function errorMessage(payload: unknown, status: number): string {
  if (status === 401) return 'Your session has expired. Sign in and try again.'
  if (payload && typeof payload === 'object') {
    const { error, errors } = payload as Record<string, unknown>
    if (typeof error === 'string' && error) return error
    const messages = Array.isArray(errors)
      ? errors
      : errors && typeof errors === 'object'
        ? Object.values(errors).flat()
        : []
    const text = messages
      .filter((message) => typeof message === 'string')
      .join(', ')
    if (text) return text
  }
  if (status === 403) return "You don't have permission to do that."
  if (status === 429) return 'Too many requests. Please try again shortly.'
  return 'Your request could not be completed. Please try again.'
}

/** JSON endpoints only; Inertia owns page visits and form submissions. */
export async function jsonRequest<T = void>(
  url: string,
  { body, ...options }: JsonRequestOptions = {}
): Promise<T> {
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')
  if (body !== undefined) headers.set('Content-Type', 'application/json')
  const method = (options.method ?? 'GET').toUpperCase()
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const token = csrfToken()
    if (!token) {
      throw new JsonRequestError(
        'Your session could not be verified. Refresh the page and try again.'
      )
    }
    headers.set('X-CSRF-Token', token)
  }

  let response: Response
  try {
    response = await fetch(url, {
      ...options,
      method,
      credentials: 'same-origin',
      mode: 'same-origin',
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
  } catch (error) {
    if (
      options.signal?.aborted ||
      (error instanceof Error && error.name === 'AbortError')
    )
      throw error
    throw new JsonRequestError(
      'Could not connect. Check your connection and try again.'
    )
  }

  if (response.status === 204) return undefined as T

  let payload: unknown
  if (
    response.headers
      .get('Content-Type')
      ?.match(/\bapplication\/(?:[\w.-]+\+)?json\b/i)
  ) {
    try {
      payload = await response.json()
    } catch (error) {
      if (options.signal?.aborted) throw error
      if (response.ok)
        throw new JsonRequestError(unexpectedResponse, response.status)
    }
  } else if (response.ok) {
    throw new JsonRequestError(unexpectedResponse, response.status)
  }
  if (!response.ok)
    throw new JsonRequestError(
      errorMessage(payload, response.status),
      response.status
    )
  return payload as T
}
